import React, { useState } from "react";
import { TrendingUp, Target, Shield } from "react-feather";
import "./MarketPositionNew.css";

const ExecutiveOverview = () => {
  const [selectedCompanyBreakdown, setSelectedCompanyBreakdown] =
    useState(null);

  // Helper function to parse markdown-style links and convert to JSX
  const parseLinks = (text) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the link
      const linkText = match[1];
      const url = match[2];
      parts.push(
          <a   
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0d6efd', textDecoration: 'none' }}
          onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
          onMouseOut={(e) => e.target.style.textDecoration = 'none'}
        >
          {linkText}
        </a>
      );
      
      lastIndex = linkRegex.lastIndex;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  const scoreBreakdown = {
    jnjmedtech: {
      overall: 9.1,
      dimensions: [
        {
          name: "Share Rank",
          score: 9.4,
          explanation:
            "MedTech sales reached $31.9B in 2024, keeping J&J the undisputed scale leader across surgical, cardiovascular and ortho settings.([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))",
        },
        {
          name: "Geographic Mix",
          score: 9.5,
          explanation:
            "Roughly 250 operating companies support distribution in 57+ countries, covering every major region and go-to-market model.([megatechthailand.com](https://megatechthailand.com/en/manufacturing-trends/surviving-digital-disruption-medical-devices-manufacturing/?utm_source=openai))",
        },
        {
          name: "Segment Mix",
          score: 9.3,
          explanation:
            "Portfolios spanning surgery, electrophysiology, vision, wound closure and heart recovery limit reliance on any single therapy line.([sec.gov](https://www.sec.gov/Archives/edgar/data/200406/000020040625000038/jnj-20241229.htm?utm_source=openai))",
        },
        {
          name: "Customer Satisfaction",
          score: 8.3,
          explanation:
            "EP, Abiomed and surgical vision lines delivered mid-single-digit growth on strong clinician demand, signaling sticky customer relationships.([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))",
        },
        {
          name: "Concentration Risk",
          score: 9,
          explanation:
            "Even with the planned DePuy Synthes spin-out, diversified cash flows within J&J's dual MedTech/Innovative Medicine structure buffer category shocks.([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-announces-intent-to-separate-its-orthopaedics-business?utm_source=openai))",
        },
      ],
    },
    stryker: {
      overall: 8.3,
      dimensions: [
        {
          name: "Share Rank",
          score: 8.6,
          explanation:
            "Delivered $22.6B revenue and 10.2% organic growth in 2024, reinforcing Stryker's position as the #2 pure-play medtech player.([thespinemarketgroup.com](https://thespinemarketgroup.com/stryker-reports-2024-operating-results-and-2025-outlook/?utm_source=openai))",
        },
        {
          name: "Geographic Mix",
          score: 8.4,
          explanation:
            "Operates 45 production sites and serves 75+ countries, balancing U.S. (75% of sales) with EMEA/APAC expansion.([marketscreener.com](https://www.marketscreener.com/quote/stock/STRYKER-CORPORATION-14536/news/Stryker-reports-2024-operating-results-and-2025-outlook-48893572/?utm_source=openai))",
        },
        {
          name: "Segment Mix",
          score: 8.7,
          explanation:
            "MedSurg & Neurotechnology (59.8%) plus Orthopaedics & Spine (40.2%) provide diversified exposure to capital equipment and implants.([marketscreener.com](https://www.marketscreener.com/quote/stock/STRYKER-CORPORATION-14536/news/Stryker-reports-2024-operating-results-and-2025-outlook-48893572/?utm_source=openai))",
        },
        {
          name: "Customer Satisfaction",
          score: 8.2,
          explanation:
            "Double-digit growth across segments and 11.9% Q1 2025 revenue gains highlight strong surgeon loyalty and service execution.([thespinemarketgroup.com](https://thespinemarketgroup.com/stryker-reports-2024-operating-results-and-2025-outlook/?utm_source=openai))",
        },
        {
          name: "Concentration Risk",
          score: 7.8,
          explanation:
            "Portfolio reshaping via the Inari acquisition and U.S. spine divestiture reduces exposure to lower-growth categories while adding venous interventions.([reuters.com](https://www.reuters.com/business/healthcare-pharmaceuticals/medical-device-maker-stryker-nearing-deal-buy-inari-medical-sources-say-2025-01-06/?utm_source=openai))",
        },
      ],
    },
    zimmer: {
      overall: 6.8,
      dimensions: [
        {
          name: "Share Rank",
          score: 7.2,
          explanation:
            "At $7.68B revenue Zimmer remains a top-five ortho implant maker but trails the scale of Stryker and J&J.([biospace.com](https://www.biospace.com/press-releases/zimmer-biomet-announces-fourth-quarter-and-full-year-2024-financial-results?utm_source=openai))",
        },
        {
          name: "Geographic Mix",
          score: 8,
          explanation:
            "Operations in 25+ countries with product sales into 100+ markets sustain access to tenders worldwide.([prnewswire.com](https://www.prnewswire.com/news-releases/zimmer-biomet-announces-third-quarter-2025-financial-results-302605060.html?utm_source=openai))",
        },
        {
          name: "Segment Mix",
          score: 6.5,
          explanation:
            "Still heavily weighted to large-joint implants despite adding Paragon 28 foot/ankle assets and Monogram robotics.([reuters.com](https://www.reuters.com/markets/deals/zimmer-biomet-buy-paragon-28-11-billion-2025-01-28/?utm_source=openai))",
        },
        {
          name: "Customer Satisfaction",
          score: 6.3,
          explanation:
            "ERP-driven shipment delays and trimmed guidance signaled service issues that dent surgeon confidence.([investopedia.com](https://www.investopedia.com/zimmer-biomet-stock-falls-as-medtech-firm-warns-of-impact-from-software-switch-8707137?utm_source=openai))",
        },
        {
          name: "Concentration Risk",
          score: 5.8,
          explanation:
            "Heavy reliance on hip/knee cycles leaves earnings sensitive to volume dips despite ROSA installs nearing 2,000 globally.([globenewswire.com](https://www.globenewswire.com/news-release/2025/07/14/3114662/0/en/Zimmer-Biomet-Announces-Definitive-Agreement-to-Acquire-Monogram-Technologies-Expanding-Robotics-Suite-with-Autonomous-Solutions.html?utm_source=openai))",
        },
      ],
    },
    smithnephew: {
      overall: 6.9,
      dimensions: [
        {
          name: "Share Rank",
          score: 6.5,
          explanation:
            "Generated $5.81B in 2024, keeping Smith+Nephew a mid-tier ortho, sports medicine and wound-care contender.([smith-nephew.com](https://www.smith-nephew.com/news/2025/02/25/20250225-smithnephew-full-year-results-2024?utm_source=openai))",
        },
        {
          name: "Geographic Mix",
          score: 7.2,
          explanation:
            "Operates in ~100 countries with manufacturing hubs across the U.S., Costa Rica, UK, Malaysia, China and Switzerland.([smith-nephew.com](https://www.smith-nephew.com/en/news/2025/04/30/20250429-smith-nephew-first-quarter-2025-trading-update?utm_source=openai))",
        },
        {
          name: "Segment Mix",
          score: 7.3,
          explanation:
            "Three balanced franchises (Orthopaedics, Sports Medicine & ENT, Advanced Wound Management) share growth contributions.([smith-nephew.com](https://www.smith-nephew.com/news/2025/02/25/20250225-smithnephew-full-year-results-2024?utm_source=openai))",
        },
        {
          name: "Customer Satisfaction",
          score: 6.8,
          explanation:
            "12-Point Plan removed 90% of overdue orders and restored set availability, improving front-line surgeon experience.([smith-nephew.com](https://www.smith-nephew.com/news/2025/02/25/20250225-smithnephew-full-year-results-2024?utm_source=openai))",
        },
        {
          name: "Concentration Risk",
          score: 6.6,
          explanation:
            "China VBP exposure and activist pressure still create volatility despite portfolio balance.([marketwatch.com](https://www.marketwatch.com/story/smith-nephew-stock-slumps-after-china-tied-profit-warning-2e33fe79?utm_source=openai))",
        },
      ],
    },
    arthrex: {
      overall: 5.8,
      dimensions: [
        {
          name: "Share Rank",
          score: 5,
          explanation:
            "Estimated ~$3B revenue keeps Arthrex influential in sports medicine yet small versus public giants.([businessobserverfl.com](https://www.businessobserverfl.com/news/2024/aug/23/naples-exec-medical-device-business/?utm_source=openai))",
        },
        {
          name: "Geographic Mix",
          score: 6,
          explanation:
            "Subsidiaries and distributors in 80+ countries with U.S., Germany and Singapore hubs support reach despite limited owned plants abroad.([arthrex.com](https://www.arthrex.com/corporate/aboutus?utm_source=openai))",
        },
        {
          name: "Segment Mix",
          score: 5.5,
          explanation:
            "Holds ~33% sports medicine share but minimal exposure to large-joint, cardio or neuro segments.([pestel-analysis.com](https://pestel-analysis.com/blogs/competitors/arthrex?utm_source=openai))",
        },
        {
          name: "Customer Satisfaction",
          score: 7.8,
          explanation:
            "160+ global training labs and high-touch Technology Consultants create outsized loyalty among arthroscopy specialists.([arthrex.com](https://www.arthrex.com/corporate/aboutus?utm_source=openai))",
        },
        {
          name: "Concentration Risk",
          score: 4.5,
          explanation:
            "Heavy reliance on elective ASC sports medicine demand leaves limited buffer against macro or reimbursement shifts.([pestel-analysis.com](https://pestel-analysis.com/blogs/competitors/arthrex?utm_source=openai))",
        },
      ],
    },
  };

  const companies = [
    {
      company: "Johnson & Johnson MedTech",
      companyId: "jnjmedtech",
      score: "9.1/10",
      status: "Dominant leader",
      insight:
        "MedTech revenue of $31.9B keeps Ethicon, Biosense Webster and Abiomed entrenched in every procedural suite.([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))",
      colorClass: "bg-danger",
      width: "91%",
    },
    {
      company: "Stryker",
      companyId: "stryker",
      score: "8.3/10",
      status: "Strong competitor",
      insight:
        "Balanced MedSurg and Ortho mix plus 45-plant supply chain sustain double-digit organic growth.([thespinemarketgroup.com](https://thespinemarketgroup.com/stryker-reports-2024-operating-results-and-2025-outlook/?utm_source=openai))",
      colorClass: "bg-primary",
      width: "83%",
    },
    {
      company: "Zimmer Biomet",
      companyId: "zimmer",
      score: "6.8/10",
      status: "Solid specialist",
      insight:
        "Large-joint focus is diversifying via Paragon 28 and Monogram robotics, but service issues linger.([reuters.com](https://www.reuters.com/markets/deals/zimmer-biomet-buy-paragon-28-11-billion-2025-01-28/?utm_source=openai))",
      colorClass: "bg-info",
      width: "68%",
    },
    {
      company: "Smith & Nephew",
      companyId: "smithnephew",
      score: "6.9/10",
      status: "Rebounding player",
      insight:
        "12-point plan restored instrument availability, yet China VBP headwinds cap upside.([smith-nephew.com](https://www.smith-nephew.com/news/2025/02/25/20250225-smithnephew-full-year-results-2024?utm_source=openai))",
      colorClass: "bg-secondary",
      width: "69%",
    },
    {
      company: "Arthrex",
      companyId: "arthrex",
      score: "5.8/10",
      status: "Niche innovator",
      insight:
        "Sports-medicine dominance and 1,000+ annual launches wow surgeons, but scale remains limited.([arthrex.com](https://www.arthrex.com/corporate/aboutus?utm_source=openai))",
      colorClass: "bg-success",
      width: "58%",
    },
  ];

  return (
    <div>
      <style>{`
        .executive-summary-card {
          border-bottom: 1px solid #e9ecef;
          background-color: #f7f9ff;
          padding: 12px;
          border: solid 1px #98a4aa33;
          border-radius: 8px;
          margin-bottom: 10px;
        }
        .exe_summary {
          font-weight: 600;
          font-size: 14px;
          line-height: 17.5px;
          color: #1e1c25;
        }
        .summary-icon-box {
          width: 3.5rem;
          height: 3.5rem;
          background: #fff;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .stat-card {
          padding: 12px 15px;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
          border: 1px solid #eee;
          position: relative;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
          text-align:center;
        }
        .score_inter{
          font-weight: 400;
          font-size: 12px;
          background-color: #f0f4fa;
          line-height: 1.4;
          color: rgba(0, 0, 0, 0.6);
          /* margin-top: 5px; */
          margin-bottom: 20px;
          padding: 12px 8px 12px 12px;
          border-radius: 8px;
        }
        .stat-card-text {
          color: #5c5c64;
          font-size: 12px;
          font-weight: 400;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .stat-card-value {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 0.25rem;
          line-height: 1;
          color: #000;

        }
        .stat-card-label {
          color: #5c5c64;
          font-size: 12px;
          font-weight: 400;

        }
        .bottom-line-card {
          padding: 12px 15px;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
          border: 1px solid #eee;
          position: relative;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 12px;
          text-align: center;
        }
        .bottom-line-card p{
          color: #5c5c64;
          font-size: 12px;
          font-weight: 400;
          margin-bottom:0px;
        }
        .sub_para{
          color: #5c5c64;
          font-size: 12px;
          font-weight: 400;
          margin-bottom:0px;
        }
        .threat-card {
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid;
          height: 100%;
        }
        .threat-card-high {
          background-color: #fef2f2;
          border: 1px solid #b91c1c29;
        }
        .number_danger, .badge_danger{
          background-color:#b91c1c !important;
          border-radius:5px !important;
        }
        .list_li{
          font-weight: 400;
          font-size: 12px;
          line-height: 17.4px;
          margin-bottom: 10px;
          color: #1e1c25;
          align-items: start;
          display: flex;
        }
        .dot_list{
          margin-right:5px;
        }
        .text-warning-med{

        }
        .score{
          font-size:12px;
        }
        .threat-card-medium {
          background-color: #eff6ff;
          border: 1px solid #2563eb29;
        }
        .number_med, .number_badge_med{
          background-color:#1e74e6 !important;
          color:#fff !important;
        }
        .number_low, .number_badge_low{
          background-color:#15803d !important;
          color:#fff !important;
        }
        .threat-card-low {
          background-color: #f0fdf4;
          border: 1px solid #15803d29;
        }
        .score-bar-container {
          height: 13px;
          background: #e9ecef;
          border-radius: 8px;
          overflow: hidden;
          position: relative;
        }
        .score-bar {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding-right: 1rem;
          transition: all 0.5s ease;
          cursor: pointer;
          font-size:12px;
        }
        .score-bar:hover {
          opacity: 0.9;
          transform: scaleY(1.05);
        }
        .company-card {
          margin-bottom: 1rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 10px 16px;
        }
        .company-card-highlight {
          background: #e7f3ff;
          border: 2px solid #0d6efd;
          box-shadow: 0 4px 10px rgba(13,110,253,0.2);
        }
        .breakdown-card {
          background: linear-gradient(135deg, #e7f3ff 0%, #eef2ff 100%);
          border-radius: 0.75rem;
          padding: 1.5rem;
          border: 2px solid #0d6efd;
          box-shadow: 0 8px 20px rgba(13,110,253,0.2);
          margin-top: 1rem;
          margin-bottom: 1rem;
        }
        .dimension-card {
          background: #fff;
          border-radius: 0.5rem;
          padding: 1rem;
          transition: all 0.3s ease;
        }
        .dimension-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .dimension-progress {
          height: 0.5rem;
          background: #e9ecef;
          border-radius: 1rem;
          overflow: hidden;
          position: relative;
        }
        .dimension-progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #0d6efd 0%, #0b5ed7 100%);
          transition: width 0.5s ease;
        }
        .priority-card {
          position: relative;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid;
          height: 100%;
        }
        .priority-number {
          position: absolute;
          top: -0.75rem;
          left: -0.75rem;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        .priority-defend {
          background-color: #fef2f2;
          border: 1px solid #B91C1C29;
        }
        .attack_number{
          background-color:#1e74e6 !important;
        }
        .defend_number{
          background-color:#b91c1c !important;
        }
        .avoid_number{
          background-color:#15803d !important;
        }
        .priority-attack {
          background-color: #eff6ff;
          border: 1px solid #2563EB29;
        }
        .priority-avoid {
          background-color: #f0fdf4;
          border: 1px solid #15803D29;
        }
        .main_head_strat{
          font-weight: 600;
          font-size: 14px;
          line-height: 17.5px;
        }
        .main_subhead_strat{
          font-weight: 600;
          font-size: 13px;
          line-height: 17.5px;
        }
        .action-item {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          font-weight: 400;
          font-size: 12px;
          line-height: 17.4px;
          margin-bottom: 10px;
          color: #1e1c25;
        }
        .company_name_item{
          font-weight: 600;
          font-size: 13px;
          line-height: 17.5px;
          color: #1e1c25;
        }
      `}</style>

      {/* Executive Summary */}
      <div className="executive-summary-card">
        <div className="d-flex align-items-start gap-2 mb-2">
          <div>
            <h2 className="exe_summary ml-1">Executive Summary</h2>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <div className="stat-card">
              <div className="stat-card-text">Stryker Strength</div>
              <div className="stat-card-value">8.3/10</div>
              <div className="stat-card-label">Strong competitor</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card">
              <div className="stat-card-text">Closest Competitor</div>
              <div className="stat-card-value">Zimmer Biomet 6.8/10</div>
              <div className="stat-card-label">Large-joint rival to watch</div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="stat-card">
              <div className="stat-card-text">Market Leader</div>
              <div className="stat-card-value">J&J MedTech 9.1/10</div>
              <div className="stat-card-label">Dominant player</div>
            </div>
          </div>
        </div>

        <div className="bottom-line-card">
          <p className="">
            <strong>Bottom Line:</strong> Stryker's 8.3/10 score reflects
            $22.6B scale, 10.2% organic growth and an expanding portfolio that
            now spans venous thromboembolism care via Inari while exiting
            slower-growth U.S. spine.{parseLinks("([thespinemarketgroup.com](https://thespinemarketgroup.com/stryker-reports-2024-operating-results-and-2025-outlook/?utm_source=openai))")}
            {" "}The $51.61B orthopedic devices market is compounding 4.8% annually,
            so sustained share gains require faster innovation cycles than
            larger rivals.{parseLinks("([marketsandmarkets.com](https://www.marketsandmarkets.com/Market-Reports/orthopedic-device-280.html?utm_source=openai))")} Johnson & Johnson's $31.9B
            MedTech arm remains the primary threat, while Zimmer Biomet's
            robotics-heavy push keeps pressure on large-joint tenders.{parseLinks("([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))")}
          </p>
        </div>
      </div>

      {/* Threat Assessment */}
      <div className="card border-0  mb-2">
        <div className="card-body">
          <h3 className="exe_summary">Threat Assessment</h3>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="threat-card threat-card-high card_high_threat">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-2">
                    <div
                      className="bg-danger text-white rounded p-2 number_danger fw-bold "
                 
                    >
                      9.1
                    </div>
                    <div>
                      <div className="fw-bold font13">J&J MedTech</div>
                      <div className="text-muted score">Score: 9.1/10</div>
                    </div>
                  </div>
                  <span className="badge bg-danger badge_danger font11">HIGH THREAT</span>
                </div>
                <div className="small">
                  <div className="mb-2 list_li">
                    <span className="text-danger-high fw-bold dot_list">• </span>
                    <span><strong>Massive scale advantage:</strong> $31.9B MedTech revenue funds premium contracting and bundling across surgery and cardio.{parseLinks("([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))")}</span>
                  </div>
                  <div className="mb-2 list_li">
                    <span className="text-danger-high fw-bold dot_list">• </span>
                    <span><strong>Everywhere footprint:</strong> 57+ country coverage puts Ethicon reps in nearly every hospital Stryker targets.{parseLinks("([megatechthailand.com](https://megatechthailand.com/en/manufacturing-trends/surviving-digital-disruption-medical-devices-manufacturing/?utm_source=openai))")}</span>
                  </div>
                  <div className="list_li">
                    <span className="text-danger-high fw-bold dot_list">• </span>
                    <span><strong>Portfolio reshuffle:</strong> DePuy spin-out could sharpen focus on higher-growth cardiology and vision where Stryker is absent.{parseLinks("([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-announces-intent-to-separate-its-orthopaedics-business?utm_source=openai))")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="threat-card threat-card-medium">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-2">
                    <div
                      className="bg-info text-white rounded p-2 fw-bold number_med">
                      6.8
                    </div>
                    <div>
                      <div className="fw-bold font13">Zimmer Biomet</div>
                      <div className="small text-muted score">Score: 6.8/10</div>
                    </div>
                  </div>
                  <span className="badge bg-warning text-dark number_badge_med font11">
                    MEDIUM THREAT
                  </span>
                </div>
                <div className="small">
                  <div className="mb-2 list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span> <strong>Robotics surge:</strong> ROSA installs nearing 2,000 create switching pressure in high-volume joint centers.{parseLinks("([globenewswire.com](https://www.globenewswire.com/news-release/2025/07/14/3114662/0/en/Zimmer-Biomet-Announces-Definitive-Agreement-to-Acquire-Monogram-Technologies-Expanding-Robotics-Suite-with-Autonomous-Solutions.html?utm_source=openai))")}</span>
                  </div>
                  <div className="mb-2 list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span><strong>Foot & ankle white space:</strong> Paragon 28 and Monogram deals broaden coverage in Stryker's focus areas.{parseLinks("([reuters.com](https://www.reuters.com/markets/deals/zimmer-biomet-buy-paragon-28-11-billion-2025-01-28/?utm_source=openai))")}</span>
                  </div>
                  <div className="list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span><strong>Execution risk:</strong> ERP-driven shipment issues show customer experience gaps Stryker can exploit.{parseLinks("([investopedia.com](https://www.investopedia.com/zimmer-biomet-stock-falls-as-medtech-firm-warns-of-impact-from-software-switch-8707137?utm_source=openai))")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="threat-card threat-card-medium">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-2">
                    <div
                      className="bg-info text-white rounded p-2 fw-bold number_med">
                      6.9
                    </div>
                    <div>
                      <div className="fw-bold font13">Smith & Nephew</div>
                      <div className="small text-muted score">Score: 6.9/10</div>
                    </div>
                  </div>
                  <span className="badge bg-warning text-dark number_badge_med font11">
                    MEDIUM THREAT
                  </span>
                </div>
                <div className="small">
                  <div className="mb-2 list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span><strong>Operational reset:</strong> 12-Point Plan improved set availability, enabling more aggressive U.S. bids.{parseLinks("([smith-nephew.com](https://www.smith-nephew.com/news/2025/02/25/20250225-smithnephew-full-year-results-2024?utm_source=openai))")}</span>
                  </div>
                  <div className="mb-2 list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span><strong>Localized supply:</strong> Plants across six countries allow tariff mitigation versus U.S.-centric supply chains.{parseLinks("([smith-nephew.com](https://www.smith-nephew.com/en/news/2025/04/30/20250429-smith-nephew-first-quarter-2025-trading-update?utm_source=openai))")}</span>
                  </div>
                  <div className="list_li">
                    <span className="text-warning-med fw-bold dot_list">• </span>
                    <span><strong>China volatility:</strong> VBP exposure can trigger price wars where Stryker should stay disciplined.{parseLinks("([marketwatch.com](https://www.marketwatch.com/story/smith-nephew-stock-slumps-after-china-tied-profit-warning-2e33fe79?utm_source=openai))")}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="threat-card threat-card-low">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex gap-2">
                    <div
                      className="bg-secondary text-white rounded p-2 fw-bold number_low"
                                   >
                      5.8
                    </div>
                    <div>
                      <div className="fw-bold font13">Arthrex</div>
                      <div className="small text-muted score">Score: 5.8/10</div>
                    </div>
                  </div>
                  <span className="badge bg-success number_badge_low font11">LOW THREAT</span>
                </div>
                <div className="small ">
                  <div className="mb-2 list_li">
                    <span className="text-success-low fw-bold dot_list">• </span>
                    <span> <strong>Focused scale:</strong> ~$3B revenue limits ability to match Stryker's enterprise deals.{parseLinks("([businessobserverfl.com](https://www.businessobserverfl.com/news/2024/aug/23/naples-exec-medical-device-business/?utm_source=openai))")}</span>
                  </div>
                  <div className="mb-2 list_li">
                    <span className="text-success-low fw-bold dot_list">• </span>
                    <span> <strong>Segment concentration:</strong> 33% sports-medicine share is impressive but niche.{parseLinks("([pestel-analysis.com](https://pestel-analysis.com/blogs/competitors/arthrex?utm_source=openai))")}</span>
                  </div>
                  <div className="list_li">
                    <span className="text-success-low fw-bold dot_list">• </span>
                    <span><strong>High-touch model:</strong> Training lab network drives loyalty yet raises cost to scale globally.{parseLinks("([arthrex.com](https://www.arthrex.com/corporate/aboutus?utm_source=openai))")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How We Compare */}
      <div className="card border-0">
        <div className="card-body">
          <h3 className="exe_summary">How We Compare</h3>
          <p className="sub_para mb-2">
            Scores reflect overall competitive strength based on scale,
            capabilities, market position, and growth trajectory.
          </p>

          <div className="sub_para mb-3 d-flex justify-content-between align-items-center">
            <div className="text-muted">
              <strong>Note:</strong> Scores based on analysis of 5 key competitors
              representing the $51.61B global orthopedic devices market that is
              compounding 4.8% annually to $68.51B by 2030.{parseLinks("([marketsandmarkets.com](https://www.marketsandmarkets.com/Market-Reports/orthopedic-device-280.html?utm_source=openai))")}
            </div>
            <div className="d-flex gap-3">
              <div className="d-flex aligners-center gap-1">
                <div
                  className="bg-danger"
                  style={{
                    width: "0.75rem",
                    height: "0.75rem",
                    borderRadius: "2px",
                  }}
                ></div>
                <span className="text-muted">Market Leader (8.5+)</span>
              </div>
              <div className="d-flex align-items-center gap-1">
                <div
                  className="bg-info"
                  style={{
                    width: "0.75rem",
                    height: "0.75rem",
                    borderRadius: "2px",
                  }}
                ></div>
                <span className="text-muted">Strong Player (7.5-8.4)</span>
              </div>
            </div>
          </div>

          {companies.map((item, idx) => (
            <div key={idx}>
              <div
                className={`company-card ${
                  idx === 1 ? "company-card-highlight" : ""
                }`}
              >
                <div className="row align-items-center">
                  <div className="col-md-3">
                    <div className="fw-bold company_name_item">{item.company}</div>
                    <div className="small text-muted sub_para">{item.status}</div>
                  </div>
                  <div className="col-md-9">
                    <div className="score-bar-container">
                      <div
                        className={`score-bar ${item.colorClass}`}
                        style={{ width: item.width }}
                        onClick={() =>
                          setSelectedCompanyBreakdown(
                            selectedCompanyBreakdown === item.companyId
                              ? null
                              : item.companyId
                          )
                        }
                        title="Click to see score breakdown"
                      >
                        <span className="text-white fw-bold fs-9">
                          {item.score}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="small text-muted fst-italic sub_para"
                  style={{ marginLeft: "26%", marginTop:"-10px" }}
                >
                  {parseLinks(item.insight)}
                </div>
              </div>

              {selectedCompanyBreakdown === item.companyId && (
                <div className="breakdown-card">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="h6 fw-bold mb-0">
                      {item.company} Score Details
                    </h4>
                    <button
                      onClick={() => setSelectedCompanyBreakdown(null)}
                      className="btn btn-dark btn-sm"
                    >
                      ✕ Close
                    </button>
                  </div>

                  <div className="bg-white rounded p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-bold">Overall Score</span>
                      <span className="display-6 fw-bold text-primary">
                        {scoreBreakdown[item.companyId].overall}/10
                      </span>
                    </div>
                    <div className="small text-muted">
                      Calculated from 5 sub-dimensions below
                    </div>
                  </div>

                  <div className="row g-3">
                    {scoreBreakdown[item.companyId].dimensions.map(
                      (dim, dimIdx) => (
                        <div key={dimIdx} className="col-12">
                          <div className="dimension-card">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  <span className="fw-semibold">
                                    {dim.name}
                                  </span>
                                  <span className="badge bg-primary">
                                    {dim.score}/10
                                  </span>
                                </div>
                                <div className="dimension-progress">
                                  <div
                                    className="dimension-progress-bar"
                                    style={{ width: `${dim.score * 10}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                            <p className="small text-muted mb-0 mt-2">
                              {parseLinks(dim.explanation)}
                            </p>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="score_inter">
            <strong>Score Interpretation:</strong> 8.5-10 = Dominant market
            leader | 7.5-8.4 = Strong competitor | 6.0-7.4 = Solid specialist |
            &lt;6.0 = Niche/emerging player
          </div>
        </div>
      </div>

      {/* Strategic Priorities */}
      <div className="card border-0 mb-4">
        <div className="card-body">
          <h3 className="exe_summary mb-3">Strategic Priorities</h3>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="priority-card priority-attack">
                <div className="priority-number priority-number-attack bg-primary text-white attack_number">1</div>
                <h4
                  className="main_head_strat"
                  style={{ minHeight: "auto" }}
                >
                  ATTACK
                </h4>
                <div className="main_subhead_strat mb-3">
                  Pursue White Space
                </div>
                <div className="small">
                  <div className="action-item">
                    <Target className="text-primary1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Accelerate vascular expansion:</strong> Integrate
                      Inari's venous thromboembolism platform to sell beyond
                      traditional ortho call points.{parseLinks("([reuters.com](https://www.reuters.com/business/healthcare-pharmaceuticals/medical-device-maker-stryker-nearing-deal-buy-inari-medical-sources-say-2025-01-06/?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <Target className="text-primary1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Own ASC-ready ortho suites:</strong> Bundle Mako,
                      ProCuity and Instruments to capture MIS demand growing
                      from $31.7B to $49.9B by 2029.{parseLinks("([globenewswire.com](https://www.globenewswire.com/news-release/2024/07/11/2911811/0/en/Minimally-Invasive-Surgical-Instruments-Market-31-7B-to-49-9B-Growth-Forecast-2024-2029-MarketsandMarkets.html?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <Target className="text-primary1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Target high-growth regions:</strong> Align EMEA
                      and APAC tenders with the orthopedic devices market's 4.8%
                      CAGR trajectory.{parseLinks("([marketsandmarkets.com](https://www.marketsandmarkets.com/Market-Reports/orthopedic-device-280.html?utm_source=openai))")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="priority-card priority-defend">
                <div className="priority-number bg-danger text-white defend_number">2</div>
                <h4
                  className="main_head_strat"
                  style={{ minHeight: "auto" }}
                >
                  DEFEND
                </h4>
                <div className="main_subhead_strat mb-3">
                  Protect Your Strengths
                </div>
                <div className="small">
                  <div className="action-item">
                    <Shield className="text-danger1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Lock in IDN ecosystems:</strong> Leverage the 45-plant
                      supply chain and 75% U.S. revenue mix to guarantee
                      delivery and service-level SLAs for key health systems.{parseLinks("([marketscreener.com](https://www.marketscreener.com/quote/stock/STRYKER-CORPORATION-14536/news/Stryker-reports-2024-operating-results-and-2025-outlook-48893572/?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <Shield className="text-danger1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Refresh MedSurg capital cycles:</strong> Use
                      double-digit organic growth momentum to keep OR
                      integration accounts standardized on Stryker.{parseLinks("([thespinemarketgroup.com](https://thespinemarketgroup.com/stryker-reports-2024-operating-results-and-2025-outlook/?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <Shield className="text-danger1 flex-shrink-0" size={16} />
                    <div>
                      <strong>Out-innovate ROSA growth:</strong> Accelerate Mako
                      software upgrades and training to counter Zimmer's
                      robotics surge.{parseLinks("([globenewswire.com](https://www.globenewswire.com/news-release/2025/07/14/3114662/0/en/Zimmer-Biomet-Announces-Definitive-Agreement-to-Acquire-Monogram-Technologies-Expanding-Robotics-Suite-with-Autonomous-Solutions.html?utm_source=openai))")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="priority-card priority-avoid">
                <div className="priority-number bg-warning text-white avoid_number">3</div>
                <h4
                  className="main_head_strat"
                  style={{ minHeight: "auto" }}
                >
                  AVOID
                </h4>
                <div className="main_subhead_strat mb-3">
                  Minimize Risk Exposure
                </div>
                <div className="small">
                  <div className="action-item">
                    <div
                      className="border border-warning1 rounded flex-shrink-0"
                      style={{ width: "1rem", height: "1rem" }}
                    ></div>
                    <div>
                      <strong>Low-margin tenders:</strong> Stay disciplined on
                      China VBP-style bids where rivals already face price
                      compression.{parseLinks("([marketwatch.com](https://www.marketwatch.com/story/smith-nephew-stock-slumps-after-china-tied-profit-warning-2e33fe79?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <div
                      className="border border-warning1 rounded flex-shrink-0"
                      style={{ width: "1rem", height: "1rem" }}
                    ></div>
                    <div>
                      <strong>Vision & wound adjacencies:</strong> Avoid
                      head-to-head battles in segments dominated by J&J's
                      diversified MedTech scale.{parseLinks("([jnj.com](https://www.jnj.com/media-center/press-releases/johnson-johnson-reports-q4-2024-and-full-year-2024-results?utm_source=openai))")}
                    </div>
                  </div>
                  <div className="action-item">
                    <div
                      className="border border-warning1 rounded flex-shrink-0"
                      style={{ width: "1rem", height: "1rem" }}
                    ></div>
                    <div>
                      <strong>Over-indexing on elective sports med:</strong>
                      Arthrex's 33% share and high-touch model make fast share
                      capture costly.{parseLinks("([pestel-analysis.com](https://pestel-analysis.com/blogs/competitors/arthrex?utm_source=openai))")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveOverview;